import { useState, useRef } from "react";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { CONTACT, QUOTE } from "@/constants/testIds";
import { toast } from "sonner";
import { usePageTitle } from "@/hooks/usePageTitle";

// Minimum seconds a real human takes to fill and submit the form. Below
// this we assume it's an auto-filler / bot and silently fake success --
// no toast error (which would help the bot iterate), no network call.
const MIN_HUMAN_SECONDS = 3;

export default function Contact() {
  usePageTitle(
    "Contact",
    "Talk to WeAutomate. Send a message, request a quote, or start an NDA conversation for OEM/ODM work."
  );
  const [tab, setTab] = useState("contact"); // contact | quote
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [quote, setQuote] = useState({ name: "", email: "", phone: "", project_type: "oem_odm", description: "", budget_range: "", timeline: "" });
  // Honeypot fields -- hidden from users, filled by naive form bots.
  const [hpContact, setHpContact] = useState("");
  const [hpQuote, setHpQuote] = useState("");
  // Time the form component mounted, for the min-fill-time heuristic.
  const mountedAt = useRef(Date.now());

  const isLikelyBot = (honeypot) => {
    if (honeypot) return true;
    const seconds = (Date.now() - mountedAt.current) / 1000;
    return seconds < MIN_HUMAN_SECONDS;
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (isLikelyBot(hpContact)) { setDone(true); return; }  // silently succeed
    setBusy(true);
    try { await api.post("/leads", { ...contact, source: "contact" }); setDone(true); toast.success("Message sent!"); }
    catch { toast.error("Could not send"); } finally { setBusy(false); }
  };
  const submitQuote = async (e) => {
    e.preventDefault();
    if (isLikelyBot(hpQuote)) { setDone(true); return; }
    setBusy(true);
    try { await api.post("/quotes", quote); setDone(true); toast.success("Quote request received!"); }
    catch { toast.error("Could not send"); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="py-32">
        <div className="mx-auto max-w-xl px-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-accent flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl mt-6">Thank you.</h1>
          <p className="mt-3 text-white/70">A specialist from WeAutomate will be in touch within one business day.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20">
      <div className="mx-auto max-w-4xl px-6 lg:px-10">
        <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Contact</div>
        <h1 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">
          Tell us what you're <span className="gradient-text">building</span>.
        </h1>
        <p className="mt-3 text-white/60">
          Tell us what you're building — we'll get back within one business day.
          <span className="ml-3 font-mono text-xs text-white/40">hello@weautomate.tech · India</span>
        </p>

        <div className="mt-8 inline-flex bg-white/5 rounded-full p-1">
          {[["contact", "Say hello"], ["quote", "Request a quote"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
                    className={`text-sm px-5 py-2 rounded-full ${tab === k ? "bg-accent" : "text-white/60 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "contact" ? (
          <form onSubmit={submitContact} className="mt-8 glass rounded-2xl p-7 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required data-testid={CONTACT.name} placeholder="Name" value={contact.name}
                     onChange={(e) => setContact({ ...contact, name: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
              <input required data-testid={CONTACT.email} type="email" placeholder="Email" value={contact.email}
                     onChange={(e) => setContact({ ...contact, email: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
              <input data-testid={CONTACT.phone} placeholder="Phone" value={contact.phone}
                     onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
              <input data-testid={CONTACT.subject} placeholder="Subject" value={contact.subject}
                     onChange={(e) => setContact({ ...contact, subject: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            </div>
            <textarea required data-testid={CONTACT.message} placeholder="How can we help?" rows={5}
                      value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            {/* Honeypot: hidden from humans (off-screen + aria-hidden + tabIndex -1),
                filled only by naive form bots. If non-empty on submit, we bail. */}
            <input
              type="text"
              name="company_website"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              value={hpContact}
              onChange={(e) => setHpContact(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />
            <button data-testid={CONTACT.submit} disabled={busy}
                    className="px-6 py-3 rounded-full gradient-bg font-medium disabled:opacity-50">
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitQuote} className="mt-8 glass rounded-2xl p-7 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required data-testid={QUOTE.name} placeholder="Name" value={quote.name}
                     onChange={(e) => setQuote({ ...quote, name: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
              <input required data-testid={QUOTE.email} type="email" placeholder="Email" value={quote.email}
                     onChange={(e) => setQuote({ ...quote, email: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
              <input data-testid={QUOTE.phone} placeholder="Phone" value={quote.phone}
                     onChange={(e) => setQuote({ ...quote, phone: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
              <select data-testid={QUOTE.projectType} value={quote.project_type}
                      onChange={(e) => setQuote({ ...quote, project_type: e.target.value })}
                      className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]">
                <option value="oem_odm">OEM / ODM</option>
                <option value="home_automation">Home automation</option>
                <option value="industrial">Industrial automation</option>
                <option value="other">Other</option>
              </select>
              <input data-testid={QUOTE.budget} placeholder="Budget range" value={quote.budget_range}
                     onChange={(e) => setQuote({ ...quote, budget_range: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
              <input data-testid={QUOTE.timeline} placeholder="Timeline" value={quote.timeline}
                     onChange={(e) => setQuote({ ...quote, timeline: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            </div>
            <textarea required data-testid={QUOTE.description} placeholder="Describe your project" rows={5}
                      value={quote.description} onChange={(e) => setQuote({ ...quote, description: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            {/* Honeypot: hidden from humans, filled only by naive bots. */}
            <input
              type="text"
              name="company_website"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              value={hpQuote}
              onChange={(e) => setHpQuote(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />
            <button data-testid={QUOTE.submit} disabled={busy}
                    className="px-6 py-3 rounded-full gradient-bg font-medium disabled:opacity-50">
              {busy ? "Submitting…" : "Submit request"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
